// Intent: SERVER push registration (stage C of agent mode) — get the Expo push
// token and hand it to the backend (app_device.expo_push_token), which is the
// only sender (ExpoPushService). First consumer: "client wrote in the chat" →
// push to the caretaker agent's phone; the same registration will later serve
// client pushes (policy expiry). Web: no-op (expo-notifications unsupported).
// NOTE: remote push does NOT arrive in Expo Go on current SDKs — a development
// build is required for real end-to-end.

import Constants from 'expo-constants';
import { Platform } from 'react-native';

import { registerDeviceApi } from '@/api/mobile-api';

let registeredOnce = false;

/**
 * Ask for permission, fetch the Expo token, register it server-side.
 * Safe to call on every screen mount — it runs at most once per app session.
 */
export async function registerForPush(): Promise<boolean> {
  if (Platform.OS === 'web' || registeredOnce) return false;
  try {
    const Notifications = await import('expo-notifications');
    const { status } = await Notifications.requestPermissionsAsync();
    if (status !== 'granted') return false;
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('chat', {
        name: 'Wiadomości',
        importance: Notifications.AndroidImportance.HIGH,
      });
    }
    const projectId = Constants.expoConfig?.extra?.eas?.projectId as string | undefined;
    if (!projectId) return false;
    const token = (await Notifications.getExpoPushTokenAsync({ projectId })).data;
    const result = await registerDeviceApi(token, Platform.OS);
    registeredOnce = result.ok;
    return result.ok;
  } catch {
    // Push is an enhancement — never let it break the screen that asked for it.
    return false;
  }
}

/**
 * Route notification taps: data.type==='chat' opens the agent-mode chat pane,
 * data.type==='chat-client' opens the CLIENT chat ("Twój opiekun odpisał" —
 * 1.2.0, the other half of chat notifications), data.type==='reminder' opens
 * the reminders list (server push about a policy date / technical inspection),
 * data.type==='referral' opens "Poleć aplikację" (talon 50 zł statuses).
 * Returns an unsubscribe fn; call once from the root layout.
 */
export async function attachPushTapRouting(
  navigate: (path: string) => void,
): Promise<(() => void) | null> {
  if (Platform.OS === 'web') return null;
  try {
    const Notifications = await import('expo-notifications');
    const route = (data: Record<string, unknown> | null | undefined) => {
      if (!data) return;
      if (data.type === 'chat' && data.conversationId != null) {
        navigate(`/agent-chat/${data.conversationId}`);
      } else if (data.type === 'chat-client') {
        navigate('/chat');
      } else if (data.type === 'reminder') {
        navigate('/reminders');
      } else if (data.type === 'referral') {
        // "Polecenie skuteczne" / "Talon czeka" — land on Moje polecenia.
        navigate('/refer');
      }
    };
    // Cold start: the tap that LAUNCHED the app.
    const last = await Notifications.getLastNotificationResponseAsync();
    route(last?.notification.request.content.data as Record<string, unknown> | undefined);
    const sub = Notifications.addNotificationResponseReceivedListener((response) => {
      route(response.notification.request.content.data as Record<string, unknown> | undefined);
    });
    return () => sub.remove();
  } catch {
    return null;
  }
}
